// The areas sheet: the drawing the Areas tab draws zones on, with every
// zone painted INTO the picture before it becomes the texture under the
// model.
//
// Painted into the raster, not drawn as separate 3D shapes, on purpose: a
// zone's points are percentages of THAT image (coordinationArea.points,
// 0-100, origin top-left), so once they are part of the picture they can
// never disagree with it - whatever the plane does to the picture (scale,
// turn, the texture's flipY) it does to the zones too. A second set of
// meshes would need its own copy of the plane's transform and would be
// right in one orientation and mirrored in the next.
//
// Keyed on area _id throughout. Several areas share a colour and three on
// SpaceX BA03 share a NAME, so neither can identify a zone.

import { paintSheetGrid } from "helpers/paintSheetGrid";

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;
// The Areas tab's own default for an area saved with no colour, so a zone
// reads the same colour here as where it was drawn. A stored colour is
// kept as it is, black included - the owner asked for the areas' colours.
export const FALLBACK_AREA_COLOR = "#3b82f6";

export function areaColor(a) {
  const c = String((a && a.color) || "").trim();
  if (!HEX.test(c)) return FALLBACK_AREA_COLOR;
  const h = c.replace("#", "").toLowerCase();
  const full = h.length === 3 ? h.split("").map((x) => x + x).join("") : h;
  return `#${full}`;
}

function rgba(hex, alpha) {
  const h = hex.replace("#", "");
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

// Percent points -> pixel points on a w x h picture. Exported for the
// test: this one line is the whole registration.
export function areaPixels(area, w, h) {
  return ((area && area.points) || [])
    .filter((p) => p && Number.isFinite(Number(p.x)) && Number.isFinite(Number(p.y)))
    .map((p) => [(Number(p.x) / 100) * w, (Number(p.y) / 100) * h]);
}

// Paint the zones onto a 2D context already holding the drawing at w x h.
// `highlightId` (an area _id) is drawn heavier, so the zone being tagged
// stands out from its neighbours of the same colour.
export function paintAreas(ctx, w, h, areas, { highlightId = null } = {}) {
  const line = Math.max(2, Math.round(Math.min(w, h) / 600));
  for (const a of areas || []) {
    const pts = areaPixels(a, w, h);
    if (pts.length < 3) continue;
    const col = areaColor(a);
    const hot = highlightId && String(a._id) === String(highlightId);
    const fill = Number.isFinite(Number(a.opacity)) ? Number(a.opacity) : 0.28;
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i += 1) ctx.lineTo(pts[i][0], pts[i][1]);
    ctx.closePath();
    ctx.fillStyle = rgba(col, hot ? Math.min(0.6, fill + 0.2) : fill);
    ctx.fill();
    ctx.lineWidth = hot ? line * 2 : line;
    ctx.strokeStyle = col;
    ctx.stroke();
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("The areas sheet would not load"));
    img.src = src;
  });
}

// The drawing at `src` with the zones painted on, as a data URL ready for
// the same TextureLoader the plain sheet goes through. Capped at maxWidth
// so a 10k px plan does not become a 400 MB texture.
// `gridLines` (page percent, api/glance/sheetGrid): the building grid painted
// over the zones, so it too is part of the picture and moves with the plane.
export async function composeAreaSheet(src, areas, { maxWidth = 4096, highlightId = null, quality = 0.92, gridLines = null } = {}) {
  const img = await loadImage(src);
  const k = Math.min(1, maxWidth / (img.naturalWidth || img.width || maxWidth));
  const w = Math.max(1, Math.round((img.naturalWidth || img.width) * k));
  const h = Math.max(1, Math.round((img.naturalHeight || img.height) * k));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  paintAreas(ctx, w, h, areas, { highlightId });
  paintSheetGrid(ctx, w, h, gridLines);
  return canvas.toDataURL("image/jpeg", quality);
}
